import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  Select,
  MenuItem,
  InputLabel,
} from '@material-ui/core';
import { Entity } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { entityFeedbackApiRef } from '@backstage-community/plugin-entity-feedback';

interface CustomFeedbackDialogProps {
  entity: Entity;
  open: boolean;
  onClose: () => void;
  title?: string;
}

export const CustomFeedbackDialog = (props: CustomFeedbackDialogProps) => {
  const { entity, open, onClose, title = 'Provide Feedback' } = props;
  const [response, setResponse] = useState('');
  const [consent, setConsent] = useState<boolean | null>(null);
  const [featureType, setFeatureType] = useState('');
  const [otherFeature, setOtherFeature] = useState('');
  const [isAnonymous, setIsAnonymous] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const feedbackApi = useApi(entityFeedbackApiRef);

  const handleSubmit = async () => {
    // Validation: must have featureType, isAnonymous, and if not anonymous must have consent
    if (!featureType || (featureType === 'Other' && !otherFeature.trim()) || isAnonymous === null) return;
    if (!isAnonymous && consent === null) return;

    setIsSubmitting(true);
    try {
      const entityRef = `${entity.kind}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;

      const finalFeatureType = featureType === 'Other' ? otherFeature : featureType;

      await feedbackApi.recordResponse(entityRef, {
        response: response || '',
        consent: isAnonymous ? false : (consent ?? undefined),
        comments: JSON.stringify({
          featureType: finalFeatureType,
          additionalComments: response || '',
          isAnonymous: isAnonymous
        })
      });

      setResponse('');
      setConsent(null);
      setFeatureType('');
      setOtherFeature('');
      setIsAnonymous(null);
      onClose();
    } catch (error) {
      // Error submitting feedback - user will see form still open to retry
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setResponse('');
    setConsent(null);
    setFeatureType('');
    setOtherFeature('');
    setIsAnonymous(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ style: { overflow: 'visible' } }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers={false} style={{ overflow: 'visible' }}>
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            Entity: {entity.metadata.name}
          </Typography>
        </Box>

        <Box mb={3}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="feature-type-label">
              Which feature is this feedback about? <span style={{ color: '#f44336' }}>*</span>
            </InputLabel>
            <Select
              labelId="feature-type-label"
              value={featureType}
              onChange={(e) => setFeatureType(e.target.value as string)}
              label="Which feature is this feedback about? *"
            >
              <MenuItem value="API">API</MenuItem>
              <MenuItem value="CI/CD">CI/CD</MenuItem>
              <MenuItem value="Docs">Docs</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {featureType === 'Other' && (
          <Box mb={3}>
            <TextField
              label="Please specify"
              variant="outlined"
              fullWidth
              value={otherFeature}
              onChange={(e) => setOtherFeature(e.target.value)}
              placeholder="Enter feature type..."
              required
            />
          </Box>
        )}

        <Box mb={3}>
          <FormLabel component="legend" required={false}>
            <Typography variant="subtitle2" style={{ marginBottom: '8px' }}>
              Feedback Response <span style={{ color: '#f44336' }}>*</span>
            </Typography>
          </FormLabel>
          <TextField
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            value={response}
            onChange={(e) => setResponse(e.target.value)}
            placeholder="Please provide your feedback about this entity..."
          />
        </Box>

        <FormControl component="fieldset" style={{ marginBottom: '16px' }}>
          <FormLabel component="legend" required={false}>
            <Typography variant="subtitle2">
              Do you want to submit this feedback anonymously? <span style={{ color: '#f44336' }}>*</span>
            </Typography>
          </FormLabel>
          <RadioGroup
            value={isAnonymous === null ? '' : isAnonymous.toString()}
            onChange={(e) => {
              setIsAnonymous(e.target.value === 'true');
              // Reset consent when changing anonymous option
              if (e.target.value === 'true') {
                setConsent(null);
              }
            }}
          >
            <FormControlLabel
              value="true"
              control={<Radio color="primary" />}
              label="Yes, submit anonymously"
            />
            <FormControlLabel
              value="false"
              control={<Radio color="primary" />}
              label="No, show my name"
            />
          </RadioGroup>
        </FormControl>

        {isAnonymous === false && (
          <FormControl component="fieldset">
            <FormLabel component="legend" required={false}>
              <Typography variant="subtitle2">
                Is it okay to contact you regarding this feedback? <span style={{ color: '#f44336' }}>*</span>
              </Typography>
            </FormLabel>
            <RadioGroup
              value={consent === null ? '' : consent.toString()}
              onChange={(e) => setConsent(e.target.value === 'true')}
            >
              <FormControlLabel
                value="true"
                control={<Radio color="primary" />}
                label="Yes, you may contact me"
              />
              <FormControlLabel
                value="false"
                control={<Radio color="primary" />}
                label="No, please do not contact me"
              />
            </RadioGroup>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="default">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={isSubmitting || !featureType || (featureType === 'Other' && !otherFeature.trim()) || isAnonymous === null || (isAnonymous === false && consent === null)}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};